import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pause,
  Play,
  Play as Run,
  Square,
  Trash2,
} from "lucide-react";
import {
  centsFromInput,
  formatDate,
  formatMoney,
  inputFromCents,
} from "@mavatech/shared";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { PropertyDialog } from "./PropertyDialog";

const TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  active: "success",
  paused: "warning",
  ended: "neutral",
};
const LABEL: Record<string, string> = {
  draft: "Entwurf",
  active: "Aktiv",
  paused: "Pausiert",
  ended: "Beendet",
};
const ACTION_LABEL: Record<string, string> = {
  none: "nur Erinnerung im Vertrag",
  create_task: "Aufgabe anlegen",
  create_quote: "Angebot (Entwurf) erzeugen",
  create_invoice: "Rechnung (Entwurf) erzeugen",
};
const iso = (d?: string | Date | null) =>
  d ? new Date(d).toISOString().slice(0, 10) : "";

export function ContractForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);

  const customers = trpc.customer.list.useQuery({ activeOnly: true });
  const existing = trpc.contract.get.useQuery(
    { id: id! },
    { enabled: !isNew, retry: false },
  );

  const [loaded, setLoaded] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("wartung");
  const [startDate, setStartDate] = useState(iso(new Date()));
  const [endDate, setEndDate] = useState("");
  const [interval, setIntervalV] = useState("yearly");
  const [amount, setAmount] = useState("0,00");
  const [taxRate, setTaxRate] = useState(20);
  const [autoAction, setAutoAction] = useState("create_task");
  const [notice, setNotice] = useState(3);
  const [autoRenew, setAutoRenew] = useState(true);
  const [bodyText, setBodyText] = useState("");
  const [propDialog, setPropDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const properties = trpc.property.list.useQuery(
    { customerId },
    { enabled: !!customerId },
  );

  useEffect(() => {
    if (isNew || !existing.data || loaded) return;
    const c = existing.data;
    setCustomerId(c.customer.id);
    setPropertyId(c.property?.id ?? "");
    setTitle(c.title);
    setType(c.type);
    setStartDate(iso(c.startDate));
    setEndDate(iso(c.endDate));
    setIntervalV(c.interval);
    setAmount(inputFromCents(c.amountNetCents));
    setTaxRate(c.taxRatePercent);
    setAutoAction(c.autoAction);
    setNotice(c.noticePeriodMonths);
    setAutoRenew(c.autoRenew);
    setBodyText(c.bodyText ?? "");
    setLoaded(true);
  }, [isNew, existing.data, loaded]);

  const payload = () => ({
    customerId,
    propertyId: propertyId || null,
    title: title.trim(),
    type: type as never,
    startDate: new Date(startDate),
    endDate: endDate ? new Date(endDate) : null,
    interval: interval as never,
    amountNetCents: centsFromInput(amount),
    taxRatePercent: taxRate,
    autoAction: autoAction as never,
    noticePeriodMonths: notice,
    autoRenew,
    bodyText: bodyText.trim() || null,
  });

  const refresh = () => {
    utils.contract.list.invalidate();
    if (id) utils.contract.get.invalidate({ id });
  };
  // Warnung bei ungespeicherten Eingaben (Zurück, Menü, Seite schließen …), siehe lib/useUnsavedGuard.tsx
  const guard = useUnsavedGuard({
    ready: isNew || loaded,
    getSnapshot: () => JSON.stringify([payload()]),
  });
  const leave = (to: string) => {
    guard.allowLeave();
    navigate(to);
  };

  const create = trpc.contract.create.useMutation({
    onSuccess: (c) => {
      refresh();
      addToast({ type: "success", title: "Vertrag angelegt" });
      leave(`/vertraege/${c.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const update = trpc.contract.update.useMutation({
    onSuccess: () => {
      guard.markSaved();
      refresh();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const activate = trpc.contract.activate.useMutation({
    onSuccess: () => {
      refresh();
      addToast({
        type: "success",
        title: "Vertrag aktiviert · Nummer vergeben",
      });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const setStatus = trpc.contract.setStatus.useMutation({ onSuccess: refresh });
  const runNow = trpc.contract.runNow.useMutation({
    onSuccess: (r) => {
      refresh();
      addToast({ type: "success", title: `Lauf ausgeführt: ${r.action}` });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const remove = trpc.contract.remove.useMutation({
    onSuccess: () => {
      utils.contract.list.invalidate();
      leave("/vertraege");
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  const status = existing.data?.status ?? "draft";
  const canSave = customerId && title.trim() && startDate;

  const onSave = () => {
    if (!canSave) return false;
    if (isNew) create.mutate(payload());
    else update.mutate({ id: id!, ...payload() });
    return true;
  };
  guard.setSave(onSave);

  // Siehe InvoiceForm.tsx (AUDIT-2026-10-01-UX.md, Punkt 1.4).
  if (!isNew && existing.isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Vertrag" />
        <div className="flex-1 flex items-center justify-center text-sm text-text-tertiary">
          Lädt …
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" {...guard.rootProps}>
      {guard.dialog}
      <PageHeader
        title={
          isNew
            ? "Neuer Vertrag"
            : existing.data?.number
              ? `Vertrag ${existing.data.number}`
              : "Vertrag (Entwurf)"
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!isNew && (
              <Badge tone={TONE[status] ?? "neutral"}>
                {LABEL[status] ?? status}
              </Badge>
            )}
            <Button variant="ghost" onClick={() => navigate("/vertraege")}>
              <ArrowLeft className="w-4 h-4" /> Verträge
            </Button>
            {!isNew && status === "draft" && (
              <Button
                variant="secondary"
                onClick={() => activate.mutate({ id: id! })}
                loading={activate.isPending}
              >
                <Play className="w-4 h-4" /> Aktivieren
              </Button>
            )}
            {!isNew && status === "active" && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => runNow.mutate({ id: id! })}
                  loading={runNow.isPending}
                >
                  <Run className="w-4 h-4" /> Jetzt ausführen
                </Button>
                <Button
                  variant="ghost"
                  onClick={() =>
                    setStatus.mutate({ id: id!, status: "paused" })
                  }
                >
                  <Pause className="w-4 h-4" /> Pausieren
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setStatus.mutate({ id: id!, status: "ended" })}
                >
                  <Square className="w-4 h-4" /> Beenden
                </Button>
              </>
            )}
            {!isNew && status === "paused" && (
              <Button
                variant="secondary"
                onClick={() => setStatus.mutate({ id: id!, status: "active" })}
              >
                <Play className="w-4 h-4" /> Fortsetzen
              </Button>
            )}
            {status !== "ended" && (
              <Button
                variant="primary"
                onClick={onSave}
                loading={create.isPending || update.isPending}
                disabled={!canSave}
              >
                Speichern
              </Button>
            )}
            {!isNew && !existing.data?.number && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6 max-w-4xl">
        <div className="grid grid-cols-3 gap-4">
          <Select
            label="Kunde"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setPropertyId("");
            }}
            options={[
              { value: "", label: "— wählen —" },
              ...(customers.data ?? []).map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ]}
          />
          <div>
            <label className="label">Objekt / Anlage</label>
            <div className="flex gap-1.5">
              <Select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                options={[
                  { value: "", label: "— keines —" },
                  ...(properties.data ?? []).map((p) => ({
                    value: p.id,
                    label: p.label,
                  })),
                ]}
              />
              <Button
                variant="ghost"
                size="sm"
                disabled={!customerId}
                onClick={() => setPropDialog(true)}
              >
                +
              </Button>
            </div>
          </div>
          <Input
            label="Betreff"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Select
            label="Art"
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={[
              { value: "wartung", label: "Wartungsvertrag" },
              { value: "service", label: "Servicevertrag" },
              { value: "sonstige", label: "Sonstige Vereinbarung" },
            ]}
          />
          <Input
            label="Beginn"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Ende (optional)"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Select
            label="Intervall"
            value={interval}
            onChange={(e) => setIntervalV(e.target.value)}
            options={[
              { value: "monthly", label: "monatlich" },
              { value: "quarterly", label: "quartalsweise" },
              { value: "semiannual", label: "halbjährlich" },
              { value: "yearly", label: "jährlich" },
            ]}
          />
          <Input
            label="Betrag netto pro Turnus (€)"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Select
            label="USt-Satz"
            value={String(taxRate)}
            onChange={(e) => setTaxRate(Number(e.target.value))}
            options={[
              { value: "20", label: "20 %" },
              { value: "13", label: "13 %" },
              { value: "10", label: "10 %" },
              { value: "0", label: "0 %" },
            ]}
          />
          <Select
            label="Bei Fälligkeit"
            value={autoAction}
            onChange={(e) => setAutoAction(e.target.value)}
            options={Object.entries(ACTION_LABEL).map(([v, l]) => ({
              value: v,
              label: l,
            }))}
          />
          <Input
            label="Kündigungsfrist (Monate)"
            type="number"
            value={notice}
            onChange={(e) => setNotice(Number(e.target.value) || 0)}
          />
          <label className="flex items-center gap-2 text-sm text-text-secondary self-end pb-1.5">
            <input
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
            />
            verlängert sich automatisch
          </label>
        </div>

        <Textarea
          label="Vertragstext / Leistungsbeschreibung"
          rows={3}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
        />

        {!isNew && existing.data && (
          <div>
            <p className="text-sm font-semibold text-text-primary mb-2">
              Läufe{" "}
              {status === "active"
                ? `· nächste Fälligkeit ${formatDate(existing.data.nextRunDate as unknown as string)}`
                : ""}
            </p>
            {!existing.data.runs.length ? (
              <p className="text-sm text-text-tertiary">Noch keine Läufe.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fällig</th>
                      <th>Aktion</th>
                      <th>Erzeugt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existing.data.runs.map((r) => (
                      <tr key={r.id}>
                        <td className="text-text-secondary">
                          {formatDate(r.dueDate as unknown as string)}
                        </td>
                        <td className="text-text-secondary">{r.action}</td>
                        <td>
                          {r.generatedRefType === "quote" &&
                          r.generatedRefId ? (
                            <a
                              className="text-accent"
                              href={`/angebote/${r.generatedRefId}`}
                            >
                              Angebot öffnen
                            </a>
                          ) : r.generatedRefType === "invoice" &&
                            r.generatedRefId ? (
                            <a
                              className="text-accent"
                              href={`/rechnungen/${r.generatedRefId}`}
                            >
                              Rechnung öffnen
                            </a>
                          ) : r.generatedRefType === "task" ? (
                            <span className="text-text-tertiary">Aufgabe</span>
                          ) : (
                            <span className="text-text-tertiary">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Vertrag löschen?"
        message="Nur Entwürfe ohne Nummer können gelöscht werden."
        confirmLabel="Löschen"
        danger
        onConfirm={() => remove.mutate({ id: id! })}
      />
      <PropertyDialog
        state={{ open: propDialog, customerId }}
        onClose={() => setPropDialog(false)}
        onSaved={(pid) => {
          utils.property.list.invalidate({ customerId });
          setPropertyId(pid);
        }}
      />
    </div>
  );
}
